import { FC, memo, useMemo } from "react";
import styles from "./HackathonCard.module.css";

import { IHackathonResponse } from "api/hackathon";

import { Col, Row, Space, Tooltip } from "antd";
import Person from "assets/svgs/Person";
import MapPin from "assets/svgs/MapPin";
import Calendar from "assets/svgs/Calendar";
import { ReactNode } from "react-markdown";
import { Tag } from "components/Tag";
import { useSearchHostGlobal, useSearchTagGlobal } from "hooks/useSearchGlobal";
import { useDeadline } from "hooks/useDeadline";
import { getStartEndFormat } from "utils/dateUtils";
import { getTotalPrize } from "utils/currency";
import { getImageUrl } from "utils/urlParser";
import { getHackathonDomain } from "utils/domain";
import { routesEnum } from "pages/Routes";
import { Completed, Ongoing, ComingSoon } from "assets/svgs";
import clsx from "clsx";
import { Button } from "components/Button";

interface Props {
  data?: IHackathonResponse;
  variant?: "vertical" | "horizontal";
  allowToHide?: boolean;
  onHideToggle?: () => void;
  isHidden?: boolean;
}

const HackathonCard: FC<Props> = ({
  data,
  variant = "vertical",
  allowToHide,
  isHidden,
  onHideToggle,
}) => {
  const doSearch = useSearchHostGlobal();
  const doSearchTag = useSearchTagGlobal();

  const {
    startDate,
    deadline,
    isEndSubmission,
    onSubmissionTime,
    isBeforeSubmission,
  } = useDeadline(data);

  const totalPrizes = useMemo(() => {
    const total = getTotalPrize(data);
    if (!total) return `non-monetary`;
    return `${total}`;
  }, [data]);

  const status = useMemo(() => {
    if (isBeforeSubmission) {
      return {
        color: "#7E00FD",
        text: "Coming Soon",
        icon: <Ongoing active />,
      };
    }
    if (onSubmissionTime) {
      return {
        color: "#FF9900",
        text: "Ongoing",
        icon: <ComingSoon active />,
      };
    }
    if (isEndSubmission) {
      return {
        color: "#A6CDBA",
        text: "Completed",
        icon: <Completed />,
      };
    }
  }, [data]);

  const participant = useMemo(() => {
    if (Array.isArray(data?.participants))
      return data?.participants?.length || 0;
    return data?.participants;
  }, []);

  const location = useMemo(() => {
    switch (data?.essentials?.types) {
      case "Public-Online/Virtual":
        return "Online Hackathon ";
      case "Public-Offline":
        return "In-Person";
      case "Internal-Online/Virtual":
        return "Invite-only Online Hackathon";
      case "Internal-Offline":
        return "Invite-only & In-Person";
      default:
        return "Online Hackathon ";
    }
  }, [data?.essentials?.types]);

  return (
    <a
      href={`${getHackathonDomain(data?.essentials?.url)}${
        routesEnum.hackathon
      }/overview/${data?._id}`}
      className="default"
    >
      <div className={styles.root}>
        <div className={clsx(styles.cardBody, styles[variant])}>
          <div className={styles.thumbnail}>
            {allowToHide && (
              <Button
                color={isHidden ? "success" : "orange"}
                className={styles.hideButton}
                onClick={(e) => {
                  e.preventDefault();
                  if (onHideToggle) {
                    onHideToggle();
                  }
                }}
              >
                {isHidden ? "Unhide" : "Hide"}
              </Button>
            )}
            {status && <Status status={status} />}
            <img
              src={
                getImageUrl(data?.design?.thumbnailImage) ||
                "/images/landing/blank.png"
              }
              alt="thumbnail"
            />
          </div>
          <div className={styles.wrapContent}>
            <Space align="baseline" size={8}>
              <span className={styles.tag}>
                <Tooltip title={totalPrizes}>{totalPrizes}</Tooltip>
              </span>
              <div className="tint-2 text-14">
                in prizes - {participant}
                &nbsp;participants
              </div>
            </Space>

            <h3 className={styles.title}>{data?.essentials?.name}</h3>

            <div className={styles.tagLine}>
              <span>{data?.essentials?.tagLine}</span>
              {/* <span className="success-2">{data?.essentials?.mailManage}</span> */}
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.wrapInfo}>
            <Row gutter={[0, 16]}>
              <Col xs={12}>
                <IconItem icon={<Person />}>
                  <span
                    onClick={(e) => {
                      e.preventDefault();
                      doSearch(data?.essentials?.host || "");
                    }}
                  >
                    {data?.essentials?.host}
                  </span>
                </IconItem>
              </Col>
              {/* <Col xs={12}>
              <IconItem icon={<ClockOutline />}>
                {currentTimezone}
                <div className={styles.timezone}>{timezone.utc}</div>
              </IconItem>
            </Col> */}
              <Col xs={12}>
                <IconItem icon={<MapPin />}>
                  {location}
                  {/* || data?.essentials?.locationAddress */}
                </IconItem>
              </Col>
              <Col xs={12}>
                <IconItem noConcat icon={<Calendar />}>
                  {getStartEndFormat(startDate, deadline)}
                </IconItem>
              </Col>
            </Row>

            <Space className="mt-24" align="start" size={13}>
              <span className="nowrap tint medium">Tags:</span>
              <Space size={8} wrap>
                {data?.essentials?.themeTags?.map((tag) => (
                  <Tag
                    key={tag}
                    type="secondary"
                    hoverable
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      doSearchTag(tag);
                    }}
                  >
                    {tag}
                  </Tag>
                ))}
              </Space>
            </Space>
          </div>
        </div>
      </div>
    </a>
  );
};

export default memo(HackathonCard);

const IconItem: FC<{ icon: ReactNode; noConcat?: boolean }> = ({
  icon,
  children,
  noConcat,
}) => (
  <Space className="nowrap" wrap={false} size={8}>
    <div className={styles.icon}>{icon}</div>
    <Tooltip title={children}>
      <span
        className={clsx(styles.textConcat, {
          [styles.noConcat]: noConcat,
        })}
      >
        {children}
      </span>
    </Tooltip>
  </Space>
);

const Status: FC<{ status: any }> = ({ status }) => (
  <div
    className={styles.status}
    style={{
      background: status?.color,
      color: status?.text === "Completed" ? "#2A2E46" : "#fff",
    }}
  >
    {status.icon}
    <span>{status?.text}</span>
  </div>
);
